import FileUploader from '@components/FileUploader';
import WarningM from '@semcore/icon/Warning/m';
import { ButtonLink } from '@semcore/ui/button';
import Spin from '@semcore/ui/spin';
import Tooltip from '@semcore/ui/tooltip';
import { Text } from '@semcore/ui/typography';
import { useBrandhubResourcesQuery } from '@src/modules/brandhub/hooks/brandhubQueries';
import { FileUploaderProps, UploadStatus } from '@src/types/files-upload-types';
import { useTranslation } from 'react-i18next';

interface ResearchFormFileUploadStatusProps {
  uploadStatus: UploadStatus;
  fileUploaderProps: FileUploaderProps;
  onSelectFromBrandHub: () => void;
}

export const ResearchFormFileUploadStatus = ({
  uploadStatus,
  fileUploaderProps,
  onSelectFromBrandHub,
}: ResearchFormFileUploadStatusProps) => {
  const { t } = useTranslation(['common', 'mailings']);

  const { data } = useBrandhubResourcesQuery({
    limit: 1,
    page: 1,
  });

  switch (uploadStatus) {
    case 'uploading':
      return (
        <div className="flex gap-1">
          <Spin size="xs" />
          <Text>
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.fileUpload.uploadInProgress',
            )}
          </Text>
        </div>
      );

    case 'error':
      return (
        <div className="flex items-center gap-1.5">
          <Text
            className="flex items-center gap-1.5"
            color="text-critical">
            <WarningM />
            {t('mailings:personalizedMailingCreatorWizard.objectivesForm.fileUpload.uploadFailed')}
          </Text>
          <FileUploader {...fileUploaderProps}>
            <ButtonLink
              data-test="re-upload-file-button"
              size={300}
              use="primary">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.fileUpload.uploadFailedTryAgain',
              )}
            </ButtonLink>
          </FileUploader>
        </div>
      );

    default:
      return (
        <div className="flex gap-1">
          <FileUploader {...fileUploaderProps}>
            <ButtonLink
              data-test="upload-document-link"
              size={300}
              use="primary">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.uploadDocument',
              )}
            </ButtonLink>
          </FileUploader>
          {t('common:or')}
          <Tooltip
            interaction={data?.resources.length === 0 ? 'hover' : 'none'}
            title={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.noResourcesInWorkspace',
            )}>
            <ButtonLink
              data-test="select-from-workspace-button"
              size={300}
              use="primary"
              onClick={onSelectFromBrandHub}>
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.research.researchLink.selectFromWorkspace',
              )}
            </ButtonLink>
          </Tooltip>
        </div>
      );
  }
};
